import {useRef, useState} from "react";
import styled from "styled-components";
import {track} from "analytics";
import {guestErrorCode, guestErrorMessage} from "api/guest.api";
import {TURNSTILE_SITE_KEY} from "conts/features";
import {useToast} from "hooks";
import {Modal} from "ui/base/modal/Modal";
import {Turnstile} from "ui/base/turnstile/Turnstile";
import {LoginModal} from "ui/blocks/AuthModals/loginModal/LoginModal";
import {ForgotPasswordModal} from "ui/blocks/AuthModals/forgotPasswordModal/forgotPasswordModal";
import {Alert, AlertIcon, LockIcon, Muted, PillButton, Spinner, tokens} from "ui/blocks/guestMessaging/GuestUI";
import {CodeEntry} from "ui/blocks/quickBooking/CodeEntry";
import {GuestDetailsFields, validateGuestDetails} from "ui/blocks/quickBooking/GuestDetailsFields";
import {useQuickBookingAuth} from "ui/blocks/quickBooking/useQuickBookingAuth";

/**
 * Quick booking on the payment page (used when the booking popup is off):
 * name, email and phone, then the 4-digit code texted to the phone. On
 * success the student is signed in and the page continues with the regular
 * payment form.
 */
export const QuickBookingForm = ({teacherId}) => {
    const {sendCode, verify} = useQuickBookingAuth(teacherId);

    const [values, setValues] = useState({name: "", email: "", phone: ""});
    const [errors, setErrors] = useState({});
    const [formError, setFormError] = useState("");
    const [accountExists, setAccountExists] = useState(false);
    const [sending, setSending] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState("");
    const [turnstileReset, setTurnstileReset] = useState(0);
    const [showVerify, setShowVerify] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const startedRef = useRef(false);

    const needsBotCheck = Boolean(TURNSTILE_SITE_KEY);

    const onChange = (field, value) => {
        if (!startedRef.current) {
            startedRef.current = true;
            track("quick_booking_form_started", {teacher_id: teacherId});
        }
        setValues((prev) => ({...prev, [field]: value}));
        if (errors[field]) setErrors((prev) => ({...prev, [field]: ""}));
        setFormError("");
        setAccountExists(false);
    };

    // Sends (or resends) the code. Returns true on success.
    const send = async () => {
        setSending(true);
        setFormError("");
        try {
            await sendCode(values, turnstileToken);
            return true;
        } catch (error) {
            const code = guestErrorCode(error);
            track("quick_booking_code_failed", {teacher_id: teacherId, error: code || "network"});
            if (code === "account_exists") {
                setShowVerify(false);
                setAccountExists(true);
            } else if (code === "too_soon") {
                return true; // a code was just sent
            } else {
                setFormError(guestErrorMessage(error, "We couldn't send the code. Check your connection and try again."));
            }
            return false;
        } finally {
            setSending(false);
            setTurnstileReset((n) => n + 1); // Turnstile tokens are single-use
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const next = validateGuestDetails(values);
        setErrors(next);
        if (sending || Object.keys(next).length || (needsBotCheck && !turnstileToken)) return;
        track("quick_booking_book_clicked", {teacher_id: teacherId});
        if (await send()) setShowVerify(true);
    };

    const onVerify = async (code) => {
        await verify(values, code);
        setShowVerify(false);
        useToast("success", "Phone confirmed. Add your payment details to send your request.");
    };

    return (
        <Wrap>
            <Form onSubmit={handleSubmit} noValidate aria-busy={sending}>
                <div>
                    <Title>Your details</Title>
                    <Subtitle>We'll text you a 4-digit code to confirm your booking.</Subtitle>
                </div>

                <GuestDetailsFields values={values} errors={errors} onChange={onChange}/>

                {needsBotCheck && (
                    <Turnstile siteKey={TURNSTILE_SITE_KEY} onToken={setTurnstileToken} resetSignal={turnstileReset}/>
                )}

                {accountExists && (
                    <Alert $tone="warning" role="alert">
                        <AlertIcon/>
                        <span>You already have a Teache account. Log in to book this class. Your selection is saved.</span>
                    </Alert>
                )}
                {formError && <Alert role="alert"><AlertIcon/><span>{formError}</span></Alert>}

                {accountExists ? (
                    <PillButton type="button" $block onClick={() => setShowLogin(true)}>Log in to book</PillButton>
                ) : (
                    <PillButton type="submit" $block disabled={sending || (needsBotCheck && !turnstileToken)}>
                        {sending ? <><Spinner $light $size={16}/> Sending code…</> : "Book Class"}
                    </PillButton>
                )}

                <Muted>
                    <LockIcon size={14}/>
                    <span>Your email and phone stay private. Instructors never see them.</span>
                </Muted>
            </Form>

            {showVerify && (
                <Modal show handleClose={() => setShowVerify(false)} maxWidth={440} minHeight={0}>
                    <VerifyBody>
                        <Title>Confirm your booking</Title>
                        <CodeEntry phone={values.phone} onVerify={onVerify} onResend={send}
                                   onChangeNumber={() => setShowVerify(false)}/>
                    </VerifyBody>
                </Modal>
            )}
            {showLogin && (
                <LoginModal
                    show={showLogin}
                    handleClose={() => setShowLogin(false)}
                    setSignupModal={() => setShowLogin(false)}
                    setForgotPasswordModal={setShowForgot}
                />
            )}
            {showForgot && (
                <ForgotPasswordModal show={showForgot} handleClose={() => setShowForgot(false)} setSigninModal={setShowLogin}/>
            )}
        </Wrap>
    );
};

const Wrap = styled.div`
    width: 100%;
    max-width: 640px;
    margin-inline: auto;
    order: 2;

    @media (min-width: 1024px) {
        order: 1;
        margin-inline: unset;
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 1rem;
    box-sizing: border-box;
    font-family: var(--font-primary);
`;

const Title = styled.h2`
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.25;
    color: ${tokens.ink};
`;

const Subtitle = styled.p`
    margin: 4px 0 0;
    font-size: 14px;
    color: ${tokens.muted};
`;

const VerifyBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
    box-sizing: border-box;
    padding-right: 8px;
    font-family: var(--font-primary);
`;
